import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, Zap } from 'lucide-react';
import ParticleSwarm from './ParticleSwarm';
import { useTr } from '../contexts/LanguageContext';

const VIDEO_URL =
  'https://d8j0ntlcm91z4.cloudfront.net/user_3A50ZOlk9uxVbCULyyuvTjaRsvL/hf_20260226_185319_e086cb7f-5695-4af2-b7ae-f42b762772ce.mp4';

const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const steps = duration / 16;
          const inc = end / steps;
          const timer = setInterval(() => {
            start += inc;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 16);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);
  return { count, ref };
};

const StatBadge = ({ value, suffix, label }) => {
  const { count, ref } = useCounter(value);
  return (
    <div ref={ref} className="glass card-3d animated-border rounded-xl px-3 sm:px-5 py-4 flex flex-col items-center gap-1 cursor-default"
      data-testid={`stat-badge-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-xl sm:text-2xl md:text-3xl font-black font-outfit" style={{ color: '#ffffff' }}>
        {count.toLocaleString()}<span style={{ color: '#30d5c8' }}>{suffix}</span>
      </div>
      <div className="text-[10px] sm:text-xs font-mono tracking-wider uppercase text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</div>
    </div>
  );
};

const TypewriterText = ({ phrases, speed = 65 }) => {
  const [text, setText] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const phrase = phrases[phraseIdx];
    let timeout;
    if (!deleting) {
      if (charIdx < phrase.length) { timeout = setTimeout(() => { setText(phrase.slice(0, charIdx + 1)); setCharIdx(c => c + 1); }, speed); }
      else { timeout = setTimeout(() => setDeleting(true), 2800); }
    } else {
      if (charIdx > 0) { timeout = setTimeout(() => { setText(phrase.slice(0, charIdx - 1)); setCharIdx(c => c - 1); }, 28); }
      else { setDeleting(false); setPhraseIdx(i => (i + 1) % phrases.length); }
    }
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, phraseIdx, phrases, speed]);

  return <span><span style={{ color: '#30d5c8' }}>{text}</span><span className="typing-cursor" /></span>;
};

const HeroSection = () => {
  const t = useTr();
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setContentVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const enPhrases = ['From 10 agents to 10,000.', 'At the speed of intelligence.', 'Trustless. Verifiable. Unstoppable.', 'Swarm-scale AI starts here.'];
  const koPhrases = ['에이전트 10개에서 10,000개까지.', '지능의 속도로.', '신뢰 없이. 검증 가능. 멈출 수 없습니다.', '스웜 규모 AI는 여기서 시작됩니다.'];

  return (
    <>
      <video data-testid="hero-video" autoPlay muted loop playsInline preload="auto" src={VIDEO_URL}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 3, pointerEvents: 'none', background: 'radial-gradient(ellipse at center, rgba(5,5,5,0.2) 0%, rgba(5,5,5,0.6) 60%, rgba(5,5,5,0.95) 100%)' }} />
      <ParticleSwarm />

      <section style={{ height: '100vh', position: 'relative', zIndex: 10, background: 'transparent' }}
        className="flex flex-col items-center justify-end pb-8 sm:pb-12 gap-5">
        <button className="hero-badge flex items-center gap-2 px-5 py-2.5 glass-intense rounded-full tag-glow cursor-pointer transition-all duration-300"
          onClick={() => document.getElementById('hero-content')?.scrollIntoView({ behavior: 'smooth' })}
          style={{ background: 'rgba(48,213,200,0.08)', border: '1px solid rgba(48,213,200,0.28)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(48,213,200,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(48,213,200,0.08)'; }}
          data-testid="hero-protocol-badge">
          <Zap size={13} color="#30d5c8" />
          <span className="text-xs font-mono tracking-widest uppercase" style={{ color: '#30d5c8' }}>
            {t('Agentic Intelligence Protocol', '에이전트 인텔리전스 프로토콜')}
          </span>
        </button>
        <div className="flex flex-col items-center gap-2 cursor-pointer transition-colors"
          style={{ color: 'rgba(255,255,255,0.38)' }}
          onClick={() => document.getElementById('hero-content')?.scrollIntoView({ behavior: 'smooth' })}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.38)'}>
          <span className="text-[10px] font-mono tracking-[0.35em] uppercase">{t('Explore', '탐색')}</span>
          <ChevronDown size={16} className="animate-bounce" />
        </div>
      </section>

      <section id="hero-content" className="py-16 sm:py-20 md:py-28 line-grid sb-bg-2"
        style={{ position: 'relative', zIndex: 10 }} data-testid="hero-content-section">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 100, background: 'linear-gradient(to bottom, transparent, #0a0a0a)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -80, left: '50%', transform: 'translateX(-50%)', width: 600, height: 200, background: 'radial-gradient(ellipse, rgba(48,213,200,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
          <div className={`transition-all duration-1000 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="section-divider" />
            <p className="text-[10px] sm:text-xs font-mono tracking-[0.3em] uppercase mb-4" style={{ color: '#30d5c8' }}>
              {t('Decentralized AI Infrastructure — $SWARM Protocol', '탈중앙화 AI 인프라 — $SWARM 프로토콜')}
            </p>
            <h1 className="font-outfit font-black leading-none tracking-tighter mb-4 sm:mb-5"
              style={{ fontSize: 'clamp(36px, 7vw, 92px)', color: '#ffffff' }}>
              {t('The Cognitive Backbone', '자율 에이전트 스웜의')}<br />
              <span className="gradient-text">{t('of Agentic Swarms', '핵심 인지 기반')}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-xl max-w-2xl leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.62)' }}>
              {t(
                'Decentralized infrastructure for deploying, coordinating, and scaling autonomous AI agent swarms.',
                '자율 AI 에이전트 스웜을 배포하고, 조율하며, 확장하기 위한 탈중앙화 인프라입니다.'
              )}
            </p>
            <p className="text-sm sm:text-base md:text-lg max-w-2xl leading-relaxed mb-8 sm:mb-10 h-7">
              <TypewriterText phrases={t(enPhrases, koPhrases)} />
            </p>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-sm sm:max-w-lg mb-8 sm:mb-10">
              <StatBadge value={50} suffix="ms" label={t('Intra-Swarm Latency', '스웜 내 지연 시간')} />
              <StatBadge value={10000} suffix="+" label={t('Agents Per Swarm', '스웜당 에이전트')} />
              <StatBadge value={500} suffix="K+" label={t('Parallel Task Executions', '병렬 작업 실행')} />
            </div>

            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a href="https://core.swarmbase.io" target="_blank" rel="noopener noreferrer"
                className="glow-btn inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded font-semibold text-sm swarm-glow transition-all duration-300"
                style={{ border: '1px solid #30d5c8', color: '#000000', background: '#30d5c8' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#55d532'; e.currentTarget.style.borderColor = '#55d532'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#30d5c8'; e.currentTarget.style.borderColor = '#30d5c8'; }}
                data-testid="hero-open-dapp-btn">
                {t('Open dApp', 'dApp 열기')} <ArrowRight size={14} />
              </a>
              <Link to="/whitepaper"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded font-semibold text-sm transition-all duration-300"
                style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#30d5c8'; e.currentTarget.style.color = '#30d5c8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
                data-testid="hero-read-whitepaper-btn">
                {t('Read Whitepaper', '백서 읽기')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
